import type { ElementType, ReactNode } from 'react';
import { CdButton } from '../../atoms/CdButton';
import { CdLink } from '../../atoms/CdLink';
import { CdLogo } from '../../atoms/CdLogo';
import { useBreakpoint } from '../../hooks';
import { CdLanguageSelect, type LanguageOption } from '../../molecules/CdLanguageSelect';
import { cx } from '../../utils';
import styles from './CdPublicHeader.module.scss';

export interface CdPublicHeaderProps {
  homeHref?: string;
  helpHref?: string;
  linkAs?: ElementType;
  languages?: readonly LanguageOption[];
  language?: string;
  onLanguageChange?: (code: string) => void;
  /** Accessible name for the language dropdown. */
  languageLabel?: string;
  onSignIn?: () => void;
  signInLabel?: string;
  helpLabel?: string;
  /** Extra content (e.g. signed-in user menu) replacing the sign-in button. */
  endSlot?: ReactNode;
  className?: string;
}

/** Logged-out header: logo · Help · language · Sign in. */
export const CdPublicHeader = ({
  homeHref = '/',
  helpHref = '/help',
  linkAs,
  languages = [],
  language,
  onLanguageChange,
  languageLabel,
  onSignIn,
  signInLabel = 'Sign in',
  helpLabel = 'Help',
  endSlot,
  className,
}: CdPublicHeaderProps) => {
  const Anchor = linkAs ?? 'a';
  // Language stays in the footer below `md`; mounting it here only on desktop avoids a hidden modal.
  const showHeaderLanguage = useBreakpoint('md') && languages.length > 0;
  return (
    <header className={cx(styles.header, className)}>
      <Anchor href={homeHref} className={styles.home} aria-label="PermiOne home">
        <CdLogo />
      </Anchor>
      <div className={styles.end}>
        <CdLink as={linkAs} href={helpHref} className={styles.desktopOnly}>
          {helpLabel}
        </CdLink>
        {showHeaderLanguage && (
          <CdLanguageSelect
            languages={languages}
            value={language}
            onChange={onLanguageChange}
            label={languageLabel}
          />
        )}
        {endSlot ??
          (onSignIn && (
            <CdButton variant="tertiary" size="sm" className={styles.signIn} onClick={onSignIn}>
              {signInLabel}
            </CdButton>
          ))}
      </div>
    </header>
  );
};
